import { useState, useEffect } from "react";
import type { ReactElement } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { MsalProvider } from "@azure/msal-react";
import type { PublicClientApplication } from "@azure/msal-browser";
import { AppInsightsContext } from "@microsoft/applicationinsights-react-js";
import type { ReactPlugin } from "@microsoft/applicationinsights-react-js";
import { authService } from "@/services/authService";
import { appInsightsService, getReactPlugin } from "@/services/appInsightsService";
import { AuthGuard } from "@/components/shared/AuthGuard/AuthGuard";
import { AuthRedirectRoute } from "@/components/shared/AuthRedirectRoute/AuthRedirectRoute";
import { LoadingSpinner } from "@/components/shared/LoadingSpinner/LoadingSpinner";
import { CurrentPageProvider } from "@/hooks/useCurrentPage";
import { useTranslations } from "@/hooks/useTranslations";
import { Layout } from "@/components/layout/Layout";
import { HomePage } from "@/pages/HomePage/HomePage";
import { ContentPage } from "@/pages/ContentPage/ContentPage";
import { WhatsNewPage } from "@/pages/WhatsNewPage/WhatsNewPage";
import { LocalePickerPage } from "@/pages/LocalePickerPage/LocalePickerPage";
import { ErrorPage } from "@/pages/ErrorPage/ErrorPage";
import "@microsoft/atlas-css/dist/index.css";
import "./globals.scss";

function LoadingAuthentication(): ReactElement {
  const { t, translations } = useTranslations();

  const message = translations ? t("app.loadingAuthentication") : "Authenticating...";
  return <LoadingSpinner message={message} fullScreen />;
}

interface IAppInsightsProviderProps {
  children: React.ReactNode;
  reactPlugin: ReactPlugin | null;
}

function AppInsightsProvider({ children, reactPlugin }: IAppInsightsProviderProps): ReactElement {
  if (reactPlugin) {
    return (
      <AppInsightsContext.Provider value={reactPlugin}>
        {children}
      </AppInsightsContext.Provider>
    );
  }
  return children as ReactElement;
}

function App(): ReactElement {
  const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);
  const [reactPlugin, setReactPlugin] = useState<ReactPlugin | null>(null);

  useEffect(() => {
    // Read optional tenant override from URL query param (e.g. ?tenantid=<guid>)
    const urlParams = new URLSearchParams(window.location.search);
    const tenantParam = urlParams.get("tenantid") ?? undefined;

    // Initialize both auth and telemetry
    Promise.allSettled([
      authService.createAuthProvider(tenantParam),
      appInsightsService.initializeApplicationInsights(),
    ])
      .then(([authResult, insightsResult]) => {
        if (authResult.status === 'fulfilled') {
          setMsalInstance(authResult.value);
        } else {
          console.error('Auth initialization failed:', authResult.reason);
        }
        
        if (insightsResult.status === 'fulfilled') {
          setReactPlugin(getReactPlugin());
        } else {
          console.error('Application Insights initialization failed:', insightsResult.reason);
        }
      })
      .catch(console.error);
  }, []);

  if (!msalInstance) {
    // Ensure the router is available so `useTranslations` (which uses `useParams`) can run.
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/redirect" element={<LoadingSpinner message="Redirecting..." fullScreen />} />
          <Route path="/:locale/*" element={<LoadingAuthentication />} />
          <Route path="/:locale/*" element={<LoadingAuthentication />} />
          <Route path="/:locale" element={<LoadingAuthentication />} />
          <Route path="*" element={<LoadingAuthentication />} />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <MsalProvider instance={msalInstance}>
      <AppInsightsProvider reactPlugin={reactPlugin}>
        <BrowserRouter>
          <Routes>
            <Route path="/redirect" element={<AuthRedirectRoute />} />
            <Route
              path="/"
              element={
                <AuthGuard>
                  <CurrentPageProvider>
                    <Layout />
                  </CurrentPageProvider>
                </AuthGuard>
              }
            >
                {/* Redirect root to default locale */}
                <Route index element={<Navigate to="/en-US" replace />} />

                {/* Locale routes */}
                <Route path="change-locale" element={<LocalePickerPage />} />
                <Route path=":locale" element={<HomePage />} />
                <Route path=":locale/*" element={<ContentPage />} />
                <Route path=":locale/whats-new" element={<WhatsNewPage />} />

                {/* Fallback route (primarily for non-locale URLs) */}
                <Route path="*" element={<ErrorPage variant="notFound" />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AppInsightsProvider>
    </MsalProvider>
  );
}

export default App;
